import { getIterableKeys, isNotEmpty, isPlainObj } from '@rehaje/shared'

import {
  SchemaIntersectionInferError,
  SchemaIntersectionTypesError,
  SchemaTransformError
} from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type {
  SchemaIntersectionSpecific,
  SchemaObjectSpecific,
  TypedSchema
} from '../../types'

export class SchemaIntersectionDefinition extends SchemaDefinition {
  __r_testTypes(schema?: SchemaIntersectionSpecific): boolean {
    for (const type of schema?.types ?? []) {
      if (
        type.type !== 'Schema.Object' &&
        type.type !== 'Schema.Intersection'
      ) {
        return false
      }
    }
    return true
  }

  override defaultsMerge(
    value: any,
    schema: SchemaIntersectionSpecific,
    initialize: boolean
  ) {
    return super.defaultsMerge(value, this.infer(value, schema), initialize)
  }

  override getIn(
    schema: SchemaIntersectionSpecific,
    path: PropertyKey[]
  ): TypedSchema {
    if (path.length === 0) return schema
    return super.getIn(this.infer({}, schema), path)
  }

  override infer(
    target: any,
    schema: SchemaIntersectionSpecific,
    path: PropertyKey[] = []
  ): SchemaObjectSpecific {
    if (!isPlainObj(target)) {
      this.raise(new SchemaIntersectionInferError(target))
    }
    if (!this.__r_testTypes(schema))
      this.raise(new SchemaIntersectionTypesError())
    const seen: string[] = []
    const properties: TypedSchema[] = []
    for (const type of schema.types ?? []) {
      const inferred = super.infer(target, type, path)
      if (inferred.type !== 'Schema.Object') continue
      for (const property of inferred.properties ?? []) {
        if (property.name) {
          if (seen.includes(property.name)) continue
          seen.push(property.name)
        }
        properties.push(property)
      }
    }
    for (const name of getIterableKeys(target)) {
      if (seen.includes(name)) continue
      seen.push(name)
      properties.push({
        ...super.inferShapeSchema(target[name]),
        name
      })
    }
    return this.__r_hoistMetadata({ properties, type: 'Schema.Object' }, schema)
  }

  override inferOnlySchema(
    schema: SchemaIntersectionSpecific,
    path: PropertyKey[]
  ): SchemaIntersectionSpecific {
    return {
      ...schema,
      types: schema.types?.map((item) => super.inferOnlySchema(item, path))
    } as SchemaIntersectionSpecific
  }

  override inferTypeScript(
    schema: SchemaIntersectionSpecific,
    path: PropertyKey[]
  ): string {
    return super.inferTypeScript(this.infer(schema.value, schema, path), path)
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaIntersectionSpecific,
    path: PropertyKey[] = []
  ) {
    if (target === null && schema.nullable) return target
    if (!isPlainObj(target)) {
      if (target !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback === undefined ? {} : schema.fallback
    }
    return super.transform(target, this.infer(target, schema, path), path)
  }

  override validate(target: any, schema: SchemaIntersectionSpecific) {
    if (this.validateMetadata(target, schema, isPlainObj, isNotEmpty)) return
    if (schema.types) {
      for (const type of schema.types) {
        if (
          type.type === 'Schema.Object' ||
          type.type === 'Schema.Intersection'
        ) {
          super.validate(target, type)
        } else {
          this.raise(new SchemaIntersectionTypesError())
        }
      }
    }
  }
}
